import React, { useEffect } from 'react';
import classNames from 'classnames';
import SimpleFooter from 'src/components/layouts/SimpleFooter/SimpleFooter';
import TopBar from 'src/components/ui/TopBar/TopBar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { ControlledInput } from 'src/components/ui/Input/Input';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styles from './styles/TrackingAuthForm.module.scss';

function TrackingOrderNumber() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const orderSchema = z.object({
    orderNumber: z
      .string()
      .min(1, `${t('tracking.required_order_number')}`)
      .max(12, `${t('tracking.max_order_number')}`),
  });

  type OrderFormData = z.infer<typeof orderSchema>;

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<OrderFormData>({
    resolver: zodResolver(orderSchema),
    mode: 'onSubmit',
    defaultValues: {
      orderNumber: '',
    },
  });
  useEffect(() => {
    const cachedValue = localStorage.getItem('orderNumber');
    if (cachedValue) {
      setValue('orderNumber', cachedValue);
    }
  }, [setValue]);

  const onSubmit = (data: OrderFormData) => {
    localStorage.setItem('orderNumber', data.orderNumber);
    const urlEncodedOrderNumber = encodeURIComponent(data.orderNumber);
    navigate(`/tracking/auth/${urlEncodedOrderNumber}`);
  };

  return (
    <div className={classNames(styles.root)}>
      <div className={styles.topbar}>
        <TopBar />
      </div>
      <div className={styles.main}>
        <div className="w-full h-16" />
        <form
          onSubmit={handleSubmit(onSubmit as any)}
          className="flex flex-col md:items-center justify-center w-full gap-60">
          <div>
            <h1 className="typo-title-36 mb-12 text-center">{t('tracking.enter_order_number')}</h1>
            <p className="typo-text-16 text-faded text-center">{t('tracking.enter_order_number_description')}</p>
          </div>
          <ControlledInput
            control={control}
            {...register('orderNumber')}
            placeholder="ORD12345678"
            className={styles.input}
            error={!!errors.orderNumber}
            errorMessage={errors.orderNumber?.message}
          />
          <Button
            title={t('tracking.submit_order_number')}
            tone={ButtonToneProps.PRIMARY_BLUE}
            className="w-full max-w-460 h-full min-h-20"
            type="submit"
          />
        </form>
        <div className="h-16" />
      </div>
      <SimpleFooter />
    </div>
  );
}

export default TrackingOrderNumber;
